import type { Category, Option, Product } from './database.types';

/**
 * Logique de gestion du menu admin (US-504) — catégories & produits.
 * Pur et testable. Intègre l'apprentissage #16 (collision de slug UNIQUE).
 */

/** Génère un slug à partir d'un nom (minuscules, sans accents, tirets). */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Slug unique pour une nouvelle catégorie/produit (apprentissage #16) :
 * si le slug de base existe déjà chez le tenant, on suffixe pour éviter la collision
 * sur la contrainte UNIQUE (tenant_id, slug).
 */
export function uniqueSlug(name: string, existingSlugs: string[], now: number = Date.now()): string {
  const base = slugify(name);
  return existingSlugs.includes(base) ? `${base}-${now}` : base;
}

/** Catégories visibles, triées par position (ordre admin). */
export function visibleCategoriesSorted(categories: Category[]): Category[] {
  return categories.filter((c) => c.is_visible).sort((a, b) => a.position - b.position);
}

/** Produits d'une catégorie, disponibles, triés par position. */
export function productsForCategory(products: Product[], categoryId: string): Product[] {
  return products
    .filter((p) => p.category_id === categoryId && p.is_available)
    .sort((a, b) => a.position - b.position);
}

/**
 * Champs d'une option DUPLIQUÉE (bouton « Dupliquer » de la bibliothèque d'options).
 * Copie fidèlement type/requis/min/max/tarification/dépendance ; nom suffixé « (copie) »
 * + slug unique (contrainte UNIQUE tenant_id, slug). Les valeurs sont recopiées à part.
 */
export function duplicateOptionFields(
  option: Option,
  existingSlugs: string[],
  now: number = Date.now(),
): Partial<Option> & { name: string; slug: string } {
  const name = `${option.name} (copie)`;
  return {
    name,
    slug: uniqueSlug(name, existingSlugs, now),
    selection: option.selection,
    is_required: option.is_required,
    min_choices: option.min_choices,
    max_choices: option.max_choices,
    position: (option.position ?? 0) + 1,
    pricing_mode: option.pricing_mode ?? 'flat',
    extra_each_cents: option.extra_each_cents ?? 0,
    depends_on_option_slug: option.depends_on_option_slug ?? null,
    depends_on_value: option.depends_on_value ?? null,
  };
}

/**
 * Union d'ids d'options en préservant l'ordre (bouton « Copier les options d'un autre
 * produit »). Garde les options déjà présentes sur le produit, ajoute à la suite celles
 * du produit source qui manquent — non destructif, idempotent (recopier n'ajoute rien).
 */
export function mergeOptionIds(current: string[], incoming: string[]): string[] {
  const seen = new Set(current);
  const out = [...current];
  for (const id of incoming) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Duplication d'un produit (bouton « ⎘ Dupliquer », critère #20) : copie + nouveau slug + nom suffixé. */
export function duplicateProduct(
  product: Product,
  existingSlugs: string[],
  now: number = Date.now(),
): Omit<Product, 'id'> {
  const name = `${product.name} (copie)`;
  return {
    tenant_id: product.tenant_id,
    category_id: product.category_id,
    slug: uniqueSlug(name, existingSlugs, now),
    name,
    description: product.description,
    image_url: product.image_url,
    base_price_cents: product.base_price_cents,
    is_available: product.is_available,
    is_featured: false,
    position: product.position + 1,
  };
}
